import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import burningHills from 'assets/videos/burningHills.mov';
import crypto from 'assets/videos/Crypto.mov';
import digital from 'assets/videos/digital.mov';
import fish from 'assets/videos/fish.mov';
import fourFloors from 'assets/videos/fourFloors.mov';
import mosh from 'assets/videos/mosh.mov';
import shouldGoToSleep from 'assets/videos/shouldGoToSleep.mov';
import transmission01 from 'assets/videos/Transmission01.mov';

import crash1_3 from 'assets/videos/crash1_3.mov';
import milk from 'assets/videos/milk.mov';
import Wired from 'assets/videos/Wired.mov';
import windowSeat from 'assets/videos/windowSeat.mov';
// import playboyHijack from 'assets/videos/playboyHijack.mov';

const PLAYLIST = [
  shouldGoToSleep,
  burningHills,
  crypto,
  digital,
  fish,
  fourFloors,
  mosh,
  transmission01,
  milk,
  crash1_3,
  Wired,
  windowSeat,
];

function shuffleIndexes(blockedFirstIndex) {
  const indexes = PLAYLIST.map((_, index) => index);
  for (let i = indexes.length - 1; i > 0; i -= 1) {
    const randomIndex = Math.floor(Math.random() * (i + 1));
    [indexes[i], indexes[randomIndex]] = [indexes[randomIndex], indexes[i]];
  }
  if (indexes.length > 1 && indexes[0] === blockedFirstIndex) {
    [indexes[0], indexes[1]] = [indexes[1], indexes[0]];
  }
  return indexes;
}

function MediaPlayer({ isFocus, onRequestSize }) {
  const videoRef = useRef(null);
  const [playOrder, setPlayOrder] = useState(() => shuffleIndexes());
  const [currentPosition, setCurrentPosition] = useState(0);
  const currentIndex = playOrder[currentPosition];

  function play() {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {});
  }

  function togglePlay() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      play();
    } else {
      video.pause();
    }
  }

  function playNext() {
    if (currentPosition < playOrder.length - 1) {
      setCurrentPosition(position => position + 1);
      return;
    }
    setPlayOrder(shuffleIndexes(currentIndex));
    setCurrentPosition(0);
  }

  function playPrevious(e) {
    e.stopPropagation();
    setCurrentPosition(
      position => (position - 1 + playOrder.length) % playOrder.length,
    );
  }

  function replay(e) {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    play();
  }

  function skip(e) {
    e.stopPropagation();
    playNext();
  }

  function resizeToVideo(e) {
    const video = e.currentTarget;
    if (!video.videoWidth || !video.videoHeight) return;
    onRequestSize({
      width: video.videoWidth,
      height: video.videoHeight,
    });
  }

  return (
    <Div>
      <video
        ref={videoRef}
        className="media-player__video"
        src={PLAYLIST[currentIndex]}
        autoPlay
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={resizeToVideo}
        onCanPlay={play}
        onClick={togglePlay}
        onEnded={playNext}
      />
      <div className="media-player__controls">
        <button
          type="button"
          onClick={playPrevious}
          aria-label="Previous video"
        >
          <span className="media-player__icon media-player__icon--previous" />
        </button>
        <button type="button" onClick={replay} aria-label="Replay video">
          <span className="media-player__icon media-player__icon--replay" />
        </button>
        <button type="button" onClick={skip} aria-label="Next video">
          <span className="media-player__icon media-player__icon--next" />
        </button>
      </div>
      {!isFocus && <div className="media-player__focus-cover" />}
    </Div>
  );
}

const Div = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 240px;
  min-height: 160px;
  overflow: hidden;
  background: #000;

  .media-player__video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }

  .media-player__controls {
    position: absolute;
    left: 50%;
    bottom: 8px;
    z-index: 3;
    display: flex;
    transform: translateX(-50%);
    gap: 5px;
    padding: 4px;
    background: rgba(0, 0, 0, 0.42);
  }

  .media-player__controls button {
    width: 24px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
  }

  .media-player__controls button:active {
    background: rgba(255, 255, 255, 0.32);
  }

  .media-player__icon {
    position: relative;
    display: block;
    color: currentColor;
  }

  .media-player__icon--previous,
  .media-player__icon--next {
    width: 14px;
    height: 10px;
  }

  .media-player__icon--previous:before,
  .media-player__icon--previous:after,
  .media-player__icon--next:before,
  .media-player__icon--next:after {
    content: '';
    position: absolute;
    top: 0;
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
  }

  .media-player__icon--previous:before,
  .media-player__icon--previous:after {
    border-right: 7px solid currentColor;
  }

  .media-player__icon--previous:before,
  .media-player__icon--next:before {
    left: 0;
  }

  .media-player__icon--previous:after,
  .media-player__icon--next:after {
    left: 6px;
  }

  .media-player__icon--next:before,
  .media-player__icon--next:after {
    border-left: 7px solid currentColor;
  }

  .media-player__icon--replay {
    width: 13px;
    height: 13px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
  }

  .media-player__icon--replay:after {
    content: '';
    position: absolute;
    right: -2px;
    top: -3px;
    width: 0;
    height: 0;
    border-left: 5px solid currentColor;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
  }

  .media-player__focus-cover {
    position: absolute;
    inset: 0;
    z-index: 4;
  }
`;

export default MediaPlayer;
